import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  Link, Stack, TextField, Typography,
} from '@mui/material';
import type { Recommendation, RecStats } from '../../services/investment-plan-service';
import { MarkdownView } from '../MarkdownView';
import { baseMoney } from '../../stores/currency-store';

const STATUS_LABEL: Record<Recommendation['status'], string> = {
  open: 'Open',
  acted: 'Followed',
  partially_acted: 'Partly followed',
  ignored: 'Not followed',
  expired: 'Expired',
  dismissed: 'Dismissed',
};

/** Jev's materiality (rebalance/DCA) or signal confidence, as "0.93". */
export function jevConfidence(r: Recommendation): string | null {
  const s = r.jev_scores ?? {};
  const v = (s.material_p ?? s.confidence) as number | undefined;
  return typeof v === 'number' ? v.toFixed(2) : null;
}

/** One recommendation: what, why (reasons + sources), Jev's score, follow-through. */
export const RecommendationCard: React.FC<{
  rec: Recommendation;
  onDismiss?: (rec: Recommendation, reason: string) => Promise<void> | void;
}> = ({ rec, onDismiss }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const conf = jevConfidence(rec);
  const followed = Math.min(100, parseFloat(rec.followed_pct) || 0);
  const closable = rec.status === 'open' || rec.status === 'partially_acted';
  return (
    <Card component="article" aria-label={rec.title} sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
          <Chip size="small" variant="outlined" label={rec.action} sx={{ fontWeight: 700,
            color: rec.action === 'SELL' ? 'var(--warn-text)' : rec.action === 'HOLD' ? 'var(--text-secondary)' : 'var(--inflow-text)' }} />
          <Chip size="small" variant="outlined" label={rec.kind} />
          <Chip size="small" variant="outlined" label={STATUS_LABEL[rec.status]} />
          {rec.high_conviction && <Chip size="small" variant="outlined" label="High conviction" sx={{ color: 'var(--info-text)', borderColor: 'var(--info-text)' }} />}
          {conf && <Chip size="small" variant="outlined" label={`Jev ${conf}`} />}
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ml: 'auto' }}>
            {rec.created_at.slice(0, 10)} · plan v{rec.plan_version} · until {rec.expires_at.slice(0, 10)}
          </Typography>
        </Stack>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 650 }}>{rec.title}</Typography>
        {rec.account_name && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>Account: {rec.account_name}</Typography>
        )}
        <Box sx={{ mt: 1 }}><MarkdownView text={rec.reasons_md} /></Box>
        {rec.sources.length > 0 && (
          <Box component="ul" sx={{ pl: 2, my: 1 }} aria-label="Sources">
            {rec.sources.slice(0, 6).map((s, i) => (
              <li key={i}>
                <Typography variant="caption">
                  {s.url ? <Link href={s.url} target="_blank" rel="noopener noreferrer">{s.title}</Link> : s.title}
                  {s.source ? ` — ${s.source}` : ''}{s.published_at ? `, ${s.published_at.slice(0, 10)}` : ''}
                </Typography>
              </li>
            ))}
          </Box>
        )}
        {rec.status !== 'open' || followed > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption">
              Followed {rec.followed_pct}%{parseFloat(rec.followed_amount) > 0 ? ` · ${baseMoney(rec.followed_amount, 0)} moved` : ''}
              {rec.outcome_pct ? ` · outcome ${parseFloat(rec.outcome_pct) > 0 ? '+' : ''}${rec.outcome_pct}%` : ''}
            </Typography>
            <LinearProgress variant="determinate" value={followed} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        ) : null}
        {rec.events.length > 1 && (
          <Box component="ol" sx={{ pl: 2, mt: 1, mb: 0 }} aria-label="History">
            {rec.events.map((e, i) => (
              <li key={i}><Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                {e.created_at.slice(0, 10)} — {e.message}
              </Typography></li>
            ))}
          </Box>
        )}
        {rec.dismissed_reason && (
          <Typography variant="caption" sx={{ display: 'block' }}>Dismissed: {rec.dismissed_reason}</Typography>
        )}
        {closable && onDismiss && (
          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setOpen(true)}>Dismiss</Button>
          </Box>
        )}
      </CardContent>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Dismiss recommendation</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Reason (optional)" value={reason}
            onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => { await onDismiss?.(rec, reason); setOpen(false); }}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

/** The list, with an explicit empty state. */
export const RecommendationList: React.FC<{
  recs: Recommendation[];
  empty: string;
  onDismiss?: (rec: Recommendation, reason: string) => Promise<void> | void;
}> = ({ recs, empty, onDismiss }) => {
  if (recs.length === 0) {
    return <Typography variant="body2" sx={{ color: 'var(--text-secondary)', py: 3 }}>{empty}</Typography>;
  }
  return <Box>{recs.map((r) => <RecommendationCard key={r.id} rec={r} onDismiss={onDismiss} />)}</Box>;
};

/** Follow-through stats: made / followed / partial / ignored, money moved, outcome. */
export const RecStatsRow: React.FC<{ stats: RecStats }> = ({ stats }) => {
  const items: [string, string][] = [
    ['Made', String(stats.made)],
    ['Open', String(stats.open)],
    ['Followed', String(stats.followed)],
    ['Partly', String(stats.partial)],
    ['Not followed', String(stats.ignored)],
    ['Follow-through', `${stats.follow_through_pct}%`],
    ['Money moved', baseMoney(stats.money_moved, 0)],
    ['Outcome (acted)', stats.outcome_acted_pct != null ? `${stats.outcome_acted_pct}%` : '—'],
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(8, 1fr)' }, gap: 2 }}
      aria-label="Follow-through stats">
      {items.map(([k, v]) => (
        <Box key={k}>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block' }}>{k}</Typography>
          <Typography className="fw-num" sx={{ fontWeight: 700 }}>{v}</Typography>
        </Box>
      ))}
    </Box>
  );
};
